import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { payoutAccountsApi, payoutRequestsApi, ApiError, type PayoutAccount, type Wallet } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus } from "lucide-react";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  token?: string | null;
  wallet: Wallet | null;
  onSuccess: () => void;
}

export function PayoutDialog({ open, onOpenChange, token, wallet, onSuccess }: Props) {
  const [accounts, setAccounts] = useState<PayoutAccount[]>([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [accountId, setAccountId] = useState<string>("");
  const [amount, setAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [addingAccount, setAddingAccount] = useState(false);
  const [newAccount, setNewAccount] = useState({ bank_name: "", account_number: "", account_name: "" });
  const [savingAccount, setSavingAccount] = useState(false);

  useEffect(() => {
    if (!open) {
      setAmount("");
      setAddingAccount(false);
      setNewAccount({ bank_name: "", account_number: "", account_name: "" });
      return;
    }
    setLoadingAccounts(true);
    payoutAccountsApi
      .list(token)
      .then((list) => {
        setAccounts(list);
        setAccountId((prev) => prev || list[0]?.id || "");
        if (list.length === 0) setAddingAccount(true);
      })
      .catch((err) => toast.error(err instanceof ApiError ? err.message : "Could not load payout accounts"))
      .finally(() => setLoadingAccounts(false));
  }, [open, token]);

  const amountKobo = Math.round(parseFloat(amount || "0") * 100);
  const available = wallet?.available_kobo ?? 0;
  const amountValid = Number.isFinite(amountKobo) && amountKobo >= 10000 && amountKobo <= available;

  const saveAccount = async () => {
    if (!newAccount.bank_name.trim() || !newAccount.account_number.trim() || !newAccount.account_name.trim()) {
      return toast.error("Bank name, account number, and account name are required");
    }
    setSavingAccount(true);
    try {
      const created = await payoutAccountsApi.create(newAccount, token);
      setAccounts((a) => [created, ...a]);
      setAccountId(created.id);
      setAddingAccount(false);
      setNewAccount({ bank_name: "", account_number: "", account_name: "" });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Could not save payout account");
    } finally {
      setSavingAccount(false);
    }
  };

  const submit = async () => {
    if (!accountId) return toast.error("Select or add a payout account");
    if (!amountValid) return toast.error(amountKobo > available ? "Amount exceeds available balance" : "Minimum payout is ₦100");
    setSubmitting(true);
    try {
      await payoutRequestsApi.create(accountId, amountKobo, token);
      toast.success("Payout request submitted — pending admin approval");
      onSuccess();
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Could not submit payout request");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Request payout</DialogTitle>
          <DialogDescription>
            Requests are reviewed by an admin and transferred manually — no amount leaves your wallet until approved and paid.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          {!addingAccount ? (
            <div>
              <Label>Payout account</Label>
              <div className="flex gap-2">
                <Select value={accountId} onValueChange={setAccountId} disabled={loadingAccounts || accounts.length === 0}>
                  <SelectTrigger className="flex-1"><SelectValue placeholder={loadingAccounts ? "Loading…" : "Select account"} /></SelectTrigger>
                  <SelectContent>
                    {accounts.map((a) => (
                      <SelectItem key={a.id} value={a.id}>{a.bank_name} · {a.account_number} ({a.account_name})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button type="button" variant="outline" size="icon" onClick={() => setAddingAccount(true)} aria-label="Add account">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-2 rounded-lg border p-3">
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">New payout account</div>
              <Input placeholder="Bank name" value={newAccount.bank_name} onChange={(e) => setNewAccount({ ...newAccount, bank_name: e.target.value })} />
              <Input placeholder="Account number" value={newAccount.account_number} onChange={(e) => setNewAccount({ ...newAccount, account_number: e.target.value })} />
              <Input placeholder="Account name" value={newAccount.account_name} onChange={(e) => setNewAccount({ ...newAccount, account_name: e.target.value })} />
              <div className="flex justify-end gap-2 pt-1">
                {accounts.length > 0 ? (
                  <Button type="button" variant="ghost" size="sm" onClick={() => setAddingAccount(false)}>Cancel</Button>
                ) : null}
                <Button type="button" size="sm" onClick={saveAccount} disabled={savingAccount}>
                  {savingAccount ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null} Save account
                </Button>
              </div>
            </div>
          )}

          <div>
            <Label>Amount (NGN)</Label>
            <Input type="number" min="100" step="1" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="5000" />
            <p className="mt-1 text-xs text-muted-foreground">Available: ₦{(available / 100).toLocaleString()}</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={submitting || addingAccount}>
            {submitting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null} Submit request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
